import React, { useState, useEffect, useRef } from 'react';
import { getVideoUrl } from '../config/cdn';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faHeart, faAward, faLeaf, faUsers, faHatChef, faUtensils } from '@fortawesome/free-solid-svg-icons';

const Nosotros = () => {
  const [currentExperience, setCurrentExperience] = useState(0);
  const videoRef = useRef(null);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Intersection Observer for video autoplay
  useEffect(() => {
    const videoElement = videoRef.current;
    
    if (!videoElement) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Video is visible, play it
            videoElement.play().catch(err => {
              console.log("Video autoplay prevented:", err);
            });
          } else {
            // Video is not visible, pause it
            videoElement.pause();
          }
        });
      },
      {
        threshold: 0.5 // Video starts playing when 50% visible
      }
    );
    
    observer.observe(videoElement);
    
    return () => {
      if (videoElement) {
        observer.unobserve(videoElement);
      }
    };
  }, []);
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentExperience((prev) => (prev + 1) % experienceItems.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const experienceItems = [
    {
      name: "Nuestra Barra",
      role: "Corazón de ADASTRA",
      experience: "Tradición y maestría",
      image: "https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Cocteleria.JPG",
      speciality: "Coctelería artesanal y servicio excepcional",
      description: "El alma de nuestro restaurante late en cada trago que preparamos, donde la tradición cubana se encuentra con la innovación moderna para crear experiencias únicas."
    },
    {
      name: "Nuestros Eventos",
      role: "Momentos Especiales",
      experience: "Celebraciones memorables",
      image: "/assets/images/eventos/cantante-femenina-turbante-rastas.jpg",
      speciality: "Música en vivo y entretenimiento auténtico",
      description: "Cada evento en ADASTRA es una celebración de la cultura cubana, con música en vivo que transporta a nuestros invitados al corazón de La Habana."
    },
    {
      name: "Nuestros Buenos Momentos",
      role: "Experiencias Compartidas",
      experience: "Memorias que perduran",
      image: "/assets/images/eventos/propietario-cliente-feliz-adastra.jpg",
      speciality: "Hospitalidad cubana y calidez humana",
      description: "Los mejores momentos nacen cuando la gastronomía excepcional se encuentra con la calidez de nuestra hospitalidad, creando recuerdos que duran para toda la vida."
    }
  ];

  const values = [
    {
      icon: faLeaf,
      title: "Ingredientes Locales",
      description: "Trabajamos con productores locales para garantizar frescura y calidad en cada plato."
    },
    {
      icon: faHeart,
      title: "Pasión Culinaria",
      description: "Cada receta lleva el amor y la dedicación de generaciones de tradición cubana."
    },
    {
      icon: faAward,
      title: "Excelencia",
      description: "Nos comprometemos a ofrecer la mejor experiencia gastronómica en cada visita."
    },
    {
      icon: faUsers,
      title: "Comunidad",
      description: "Somos parte de la comunidad y contribuimos al crecimiento cultural y gastronómico local."
    }
  ];

  return (
    <div className="page-transition">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32">
        {/* Background Video */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          >
            <source src={getVideoUrl("Video para la pagina de contacto .mp4")} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/40"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center text-white px-6">
          <h1 className="font-sans text-5xl md:text-7xl font-light leading-tight tracking-wide mb-6">
            <span className="text-green-400 font-normal italic">Historia</span>
          </h1>
          <p className="font-sans text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-8">
            Más que un restaurante, somos guardianes de la tradición culinaria cubana, 
            llevando sabores auténticos a una nueva generación.
          </p>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto mt-12">
            <div className="text-center">
              <div className="font-sans text-3xl font-light text-green-400 mb-2">2024</div>
              <div className="font-sans text-sm text-white/80 uppercase tracking-wider">Fundado</div>
            </div>
            <div className="text-center">
              <div className="font-sans text-3xl font-light text-green-400 mb-2">+20</div>
              <div className="font-sans text-sm text-white/80 uppercase tracking-wider">Equipo</div>
            </div>
            <div className="text-center">
              <div className="font-sans text-3xl font-light text-green-400 mb-2">3K+</div>
              <div className="font-sans text-sm text-white/80 uppercase tracking-wider">Clientes</div>
            </div>
          </div>
        </div>
      </section>

      {/* Chef Section - White Background with Video Left */}
      <section className="py-20 bg-white">
        <div className="container-custom max-w-7xl mx-auto px-6">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            
            {/* Left - Video */}
            <div className="relative rounded-lg overflow-hidden shadow-xl bg-gray-900">
              <video 
                ref={videoRef}
                className="w-full object-cover"
                style={{ maxHeight: '750px' }}
                controls
                preload="metadata"
                muted
                loop
                playsInline
              >
                <source src={getVideoUrl("Juank Chef.MOV")} type="video/quicktime" />
                <source src={getVideoUrl("Juank Chef.MOV")} type="video/mp4" />
                Tu navegador no soporta el elemento de video.
              </video>
            </div>

            {/* Right - Content */}
            <div className="py-8">
              
              {/* Title */}
              <div className="mb-6">
                <p className="font-sans text-green-600 text-xs md:text-sm uppercase tracking-widest mb-3 font-medium">
                  Maestro Culinario
                </p>
                <h2 className="font-sans text-4xl md:text-5xl lg:text-6xl font-light text-gray-900 leading-tight mb-4">
                  Juan Carlos Estenoz
                </h2>
                {/* Decorative Line */}
                <div className="w-24 h-0.5 bg-gradient-to-r from-green-600 via-green-400 to-transparent"></div>
              </div>
              
              {/* Biography Content */}
              <div className="space-y-4 text-gray-700 font-sans text-base md:text-lg leading-relaxed">
                
                <h3 className="text-xl md:text-2xl font-light text-gray-900 mb-4">
                  La historia detrás de nuestros sabores
                </h3>
                
                <p>
                  Desde los 17 años, la cocina fue su destino. Todo comenzó en la antigua escuela de gastronomía de Cojímar, donde las primeras clases se mezclaban con el aroma del carbón y la ilusión de aprender.
                </p>
                
                <p>
                  Con el tiempo, llegaron los retos y las oportunidades. Cada cocina, cada fogón, le regaló una lección: disciplina, pasión y humildad. Dirigió la cocina de importantes restaurantes y en el histórico <span className="text-gray-900 font-medium">Hotel Sevilla</span> fue estudiante, formándose entre sabores clásicos y técnicas que marcaron su carrera.
                </p>
                
                <p>
                  Su inspiración más profunda nace de los recuerdos del campo, de sus abuelas cocinando con amor y casi sin recursos, logrando los sabores más auténticos. En cada plato busca capturar esa esencia — la <span className="text-green-600 font-medium">memoria de lo simple</span>, elevada con las <span className="text-green-600 font-medium">tendencias contemporáneas</span>.
                </p>
                
                <p className="text-gray-600 italic text-sm md:text-base pt-4">
                  "Porque más allá de las recetas, lo que cocina es historia, emoción… y un pedazo de su vida."
                </p>
                
              </div>
              
              {/* Bottom Quote */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-green-600 text-lg md:text-xl font-light italic">
                  "Cocinar es el arte de transformar ingredientes en emociones"
                </p>
              </div>
              
            </div>
            
          </div>
          
        </div>
      </section>

      {/* Nuestra Historia Section */}
      <section className="pt-8 pb-20 bg-white">
        <div className="container-custom max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Left - Text Content */}
            <div>
              <h2 className="font-sans text-5xl md:text-6xl font-light text-gray-900 leading-tight mb-8">
                Un sueño convertido en <span className="text-green-600 font-normal italic">realidad</span>
              </h2>
              
              <div className="space-y-6 text-gray-700">
                <p className="font-sans text-lg leading-relaxed">
                  ADASTRA nació en 2024 del sueño de llevar la auténtica cocina cubana a una experiencia gastronómica moderna. 
                  Inspirados por las recetas familiares que han pasado de generación en generación, decidimos crear un espacio 
                  donde la tradición y la innovación se encuentran.
                </p>
                
                <p className="font-sans text-lg leading-relaxed">
                  Nuestro nombre, que significa "hacia las estrellas" en latín, refleja nuestra ambición de elevar 
                  la gastronomía cubana a nuevas alturas, sin perder nunca de vista nuestras raíces y la calidez 
                  que caracteriza a nuestra cultura.
                </p>
                
                <p className="font-sans text-lg leading-relaxed">
                  Cada plato que servimos cuenta una historia, cada ingrediente tiene un propósito, y cada experiencia 
                  está diseñada para transportar a nuestros comensales a las calles coloridas de La Habana.
                </p>
              </div>
              
              {/* Button */}
              <div className="mt-8">
                <Link 
                  to="/reservas" 
                  className="bg-white text-black border border-gray-300 py-2 md:py-3 px-6 md:px-8 rounded-full text-sm md:text-base font-medium hover:bg-gray-100 transition-all duration-300 inline-flex items-center justify-center space-x-2 shadow-md"
                >
                  <span>VIVE LA EXPERIENCIA</span>
                  <span>→</span>
                </Link>
              </div>
            </div>
            
            {/* Right - Image */}
            <div className="relative">
              <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                <img 
                  src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Barra%20desde%20puerta%20de%20salon.JPG" 
                  alt="Interior del restaurante ADASTRA"
                  className="w-full h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
              </div>
              
              {/* Floating Stats Card */}
              <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faAward} className="text-white" />
                  </div>
                  <div>
                    <div className="font-sans text-2xl font-light text-gray-900">4.9★</div>
                    <div className="font-sans text-sm text-gray-500">Calificación promedio</div>
                  </div>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* Experiences Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute top-10 left-10 w-32 h-32 bg-green-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-green-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container-custom max-w-7xl mx-auto px-6 relative z-10">
          
          {/* Section Header */}
          <div className="text-center mb-20">
            <h2 className="font-sans text-5xl md:text-6xl font-light text-gray-900 leading-tight mb-6">
              Nuestras <span className="text-green-600 font-normal italic">Experiencias</span>
            </h2>
            <p className="font-sans text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Los elementos que hacen de ADASTRA un lugar único y especial
            </p>
          </div>

          {/* Experience Showcase */}
          <div className="max-w-5xl mx-auto">
            <div className="relative bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              {/* Gradient Background */}
              <div className="absolute inset-0 bg-gradient-to-r from-green-50 via-white to-green-50 opacity-50"></div>
              
              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-0">
                
                {/* Left - Image */}
                <div className="relative h-[500px] lg:h-auto overflow-hidden">
                  <img 
                    src={experienceItems[currentExperience]?.image} 
                    alt={experienceItems[currentExperience]?.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  
                  {/* Badge */}
                  <div className="absolute top-6 left-6 bg-green-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                    {experienceItems[currentExperience]?.role}
                  </div>
                </div>
                
                {/* Right - Content */}
                <div className="p-12 lg:p-16 flex flex-col justify-center">
                  <div className="mb-6">
                    <h3 className="font-sans text-3xl font-light text-gray-900 mb-2">
                      {experienceItems[currentExperience]?.name}
                    </h3>
                    <p className="font-sans text-lg text-green-600 font-medium mb-2">
                      {experienceItems[currentExperience]?.speciality}
                    </p>
                    <p className="font-sans text-sm text-gray-500 uppercase tracking-wider">
                      {experienceItems[currentExperience]?.experience}
                    </p>
                  </div>
                  
                  <blockquote className="font-sans text-xl text-gray-700 leading-relaxed mb-8 italic">
                    "{experienceItems[currentExperience]?.description}"
                  </blockquote>
                  
                  {/* Navigation */}
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-3">
                      {experienceItems.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentExperience(index)}
                          className={`w-4 h-4 rounded-full transition-all duration-300 ${
                            index === currentExperience 
                              ? 'bg-green-600 scale-110' 
                              : 'bg-gray-300 hover:bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>
                    
                    <div className="flex space-x-3">
                      <button 
                        onClick={() => setCurrentExperience((prev) => (prev - 1 + experienceItems.length) % experienceItems.length)}
                        className="w-12 h-12 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center hover:border-green-600 hover:text-green-600 transition-colors shadow-sm"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => setCurrentExperience((prev) => (prev + 1) % experienceItems.length)}
                        className="w-12 h-12 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center hover:border-green-600 hover:text-green-600 transition-colors shadow-sm"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Philosophy Section */}
      <section className="bg-black py-20 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Mesa Completa.JPG" 
            alt="Preparación culinaria artesanal ADASTRA"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        
        <div className="container-custom max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Left Side - Philosophy */}
            <div className="space-y-8">
              <div>
                <h2 className="font-sans text-5xl md:text-6xl font-light text-white leading-tight">
                  Nuestra <span className="text-green-400 font-normal italic">Filosofía</span>
                </h2>
              </div>
              
              <div className="space-y-6">
                <p className="font-sans text-lg text-gray-200 leading-relaxed">
                  Creemos que la cocina es el corazón de la cultura. Cada receta que preparamos 
                  lleva consigo historias, tradiciones y el alma de nuestro pueblo cubano.
                </p>
                
                <p className="font-sans text-lg text-gray-200 leading-relaxed">
                  No solo servimos comida, creamos experiencias que conectan a las personas 
                  con sus raíces, despiertan emociones y construyen memorias duraderas.
                </p>
              </div>
              
              {/* Quote */}
              <div className="border-l-4 border-green-400 pl-6 my-8">
                <blockquote className="font-sans text-2xl font-light text-white italic leading-relaxed">
                  "La cocina cubana no es solo ingredientes, es amor servido en cada plato."
                </blockquote>
                <cite className="font-sans text-sm text-gray-400 mt-4 block">- Miriam Fuentes, Chef Ejecutivo</cite>
              </div>
              
              {/* Bottom Stats */}
              <div className="flex flex-wrap items-center gap-8 pt-8">
                <div className="text-gray-300 font-sans font-medium text-sm">TRADICIÓN</div>
                <div className="text-gray-300 font-sans font-medium text-sm">INNOVACIÓN</div>
                <div className="text-gray-300 font-sans font-medium text-sm">PASIÓN</div>
                <div className="text-gray-300 font-sans font-medium text-sm">EXCELENCIA</div>
              </div>
            </div>
            
            {/* Right Side - Values Grid */}
            <div className="space-y-6">
              <div className="mb-10">
                <h3 className="font-sans text-4xl md:text-5xl font-light text-white leading-tight mb-3">
                  Nuestros Valores
                </h3>
                <div className="w-16 h-0.5 bg-gradient-to-r from-green-400 to-transparent"></div>
              </div>

              {/* Values Cards */}
              {values.map((value, index) => (
                <div key={index} className="group">
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 hover:border-white/20 transition-all duration-300 p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-green-500 transition-colors">
                        <FontAwesomeIcon icon={value.icon} className="text-white" />
                      </div>
                      <div>
                        <h4 className="font-sans text-xl font-medium text-white mb-2 group-hover:text-green-400 transition-colors">
                          {value.title}
                        </h4>
                        <p className="font-sans text-gray-300 leading-relaxed">
                          {value.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
          </div>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="container-custom max-w-7xl mx-auto px-6">
          
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="font-sans text-5xl md:text-6xl font-light text-gray-900 leading-tight mb-6">
              Detrás de <span className="text-green-600 font-normal italic">Escena</span>
            </h2>
            <p className="font-sans text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Un vistazo a la pasión y dedicación que ponemos en cada detalle
            </p>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 h-[600px]">
            
            {/* Large image - Barra */}
            <div className="col-span-2 row-span-2 relative overflow-hidden rounded-3xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Barra%2012%20del%20dia.JPG" 
                alt="Barra ADASTRA durante el día"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-6 left-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <h3 className="font-sans text-2xl font-medium mb-2">Nuestra Barra</h3>
                <p className="font-sans text-sm text-gray-200">Donde la magia sucede</p>
              </div>
            </div>

            {/* Patio del dia */}
            <div className="relative overflow-hidden rounded-2xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Patio%2012%20del%20dia.JPG" 
                alt="Patio del restaurante ADASTRA"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Ceviche de Pescado */}
            <div className="relative overflow-hidden rounded-2xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Ceviche%20de%20Pescado.JPG" 
                alt="Ceviche de Pescado especialidad"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Mariscada */}
            <div className="relative overflow-hidden rounded-2xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Mariscada.JPG" 
                alt="Mariscada fresca del Caribe"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Restaurant ambiance */}
            <div className="relative overflow-hidden rounded-2xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Patio%2012%20del%20dia.JPG" 
                alt="Ambiente del restaurante ADASTRA"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

          </div>

        </div>
      </section>

      {/* Call to Action Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden py-28">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="/assets/images/restaurant/Hero.jpg" 
            alt="Experiencia gastronómica ADASTRA"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        {/* Content */}
        <div className="container-custom max-w-7xl mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center">
            <h2 className="font-sans text-3xl md:text-6xl font-light text-white leading-tight mb-4 md:mb-6">
              Forma parte de nuestra <span className="text-green-400 font-normal italic">Historia</span>
            </h2>
            <p className="font-sans text-base md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed mb-8 md:mb-12 px-4">
              Te invitamos a vivir la experiencia ADASTRA y descubrir por qué somos 
              más que un restaurante: somos un pedazo de Cuba en cada bocado.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link 
                to="/reservas" 
                className="border border-white text-white px-8 py-3 font-bold tracking-wide hover:bg-white hover:text-black transition-all duration-300 inline-flex items-center justify-center w-auto"
              >
                <span>RESERVAR MESA</span>
              </Link>
              <Link 
                to="/menu" 
                className="border border-white text-white px-8 py-3 font-bold tracking-wide hover:bg-white hover:text-black transition-all duration-300 inline-flex items-center justify-center w-auto"
              >
                <span>VER MENÚ</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Nosotros;